// CommunityEngagement — community presence tracking dashboard
// Sections: KPI row, event calendar/list, volume trend, type distribution, station activity, social media

import { useMemo, useState } from "react";
import { useCommunityEvents } from "../hooks/useOps";
import { useAggregates } from "../hooks/useDashboard";
import { useSocialSummary, useSocialMetrics } from "../hooks/useSocial";
import type { SocialMetricRow } from "../hooks/useSocial";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import StatusBadge from "../components/dashboard/StatusBadge";
import { useTheme } from "../hooks/useTheme";
import DateRangeToggle, {
  useSinceDate,
  isAfterSince,
} from "../components/dashboard/DateRangeToggle";
import type { DateRange } from "../components/dashboard/DateRangeToggle";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import type { CommunityEventRow } from "../hooks/useOps";

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isUpcoming(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) > new Date();
}

// Note: CommunityEventRow uses date_start (from D1 dash_community_events, synced from airtable_CE_Requests Date_Start)

/**
 * ── LIVE COLUMN NAMES ────────────────────────────────────────────────────────
 *
 * dash_community_events spells the event name `Title`, its type `Type_of_Event`
 * and the head count `Number_of_Attendees` (normalized to `title`,
 * `type_of_event`, `number_of_attendees` on the wire). This page read
 * `event_name`, `event_type` and `attendance` — none of which exist on the
 * table. `CommunityEventRow` carried an index signature, so all three compiled
 * clean and evaluated to `undefined`, and across all 92 live events the name
 * and attendance columns rendered "—" while the type-distribution chart
 * bucketed every single event into "Other".
 *
 * The shape is CLOSED now; these accessors are the one place the mapping
 * lives.
 */

/** Event name — the live column is `Title`. */
export function eventTitle(e: CommunityEventRow): string | null {
  return e.title ?? null;
}

/** Head count — the live column is `Number_of_Attendees`. */
export function eventAttendance(e: CommunityEventRow): number | null {
  return typeof e.number_of_attendees === "number"
    ? e.number_of_attendees
    : null;
}

/** Normalize event type to one of the canonical buckets */
export function normalizeEventType(raw: string | null | undefined): string {
  if (!raw) return "Other";
  const lower = raw.toLowerCase();
  if (lower.includes("fire safety") || lower.includes("fire prevention"))
    return "General Fire Safety";
  if (lower.includes("station tour") || lower.includes("tour"))
    return "Station Tour";
  if (lower.includes("private")) return "Private Event";
  return "Other";
}

/** Build monthly event count for the past 12 months */
function buildMonthlyVolume(
  events: CommunityEventRow[],
): { month: string; count: number }[] {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    const count = events.filter((e) => {
      if (!e.date_start) return false;
      const ed = new Date(e.date_start);
      return ed.getFullYear() === year && ed.getMonth() === month;
    }).length;
    const label =
      d.toLocaleString("en-US", { month: "short" }) +
      ` '${String(year).slice(2)}`;
    return { month: label, count };
  });
}

/** Build event type distribution */
export function buildTypeDistribution(
  events: CommunityEventRow[],
): { name: string; value: number }[] {
  const buckets: Record<string, number> = {
    "General Fire Safety": 0,
    "Station Tour": 0,
    "Private Event": 0,
    Other: 0,
  };
  for (const e of events) {
    const type = normalizeEventType(e.type_of_event);
    buckets[type] = (buckets[type] ?? 0) + 1;
  }
  return Object.entries(buckets)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }));
}

/** Build station activity counts */
export function buildStationActivity(
  events: CommunityEventRow[],
): { station: string; count: number }[] {
  const counts: Record<string, number> = {};
  for (const e of events) {
    const station = e.station ?? "Unknown";
    counts[station] = (counts[station] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([station, count]) => ({ station, count }))
    .sort((a, b) => b.count - a.count);
}

// ── Color palette for pie chart ───────────────────────────────────────────────

const PIE_COLORS: Record<string, string> = {
  "General Fire Safety": "#2563eb",
  "Station Tour": "#16a34a",
  "Private Event": "#9333ea",
  Other: "#6b7280",
};

const PIE_FALLBACK_COLORS = [
  "#2563eb",
  "#16a34a",
  "#9333ea",
  "#6b7280",
  "#f59e0b",
];

// ── Tooltip components ────────────────────────────────────────────────────────

interface TooltipProps {
  active?: boolean;
  payload?: { name: string; value: number; color?: string }[];
  label?: string;
}

function VolumeTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg min-w-[110px]">
      <p className="font-semibold text-theme-primary mb-1">{label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Events</span>
        <span className="font-bold text-blue-500">
          {payload[0]?.value ?? 0}
        </span>
      </div>
    </div>
  );
}

function StationTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg min-w-[130px]">
      <p className="font-semibold text-theme-primary mb-1">{label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Events</span>
        <span className="font-bold text-blue-500">
          {payload[0]?.value ?? 0}
        </span>
      </div>
    </div>
  );
}

function PieTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg min-w-[130px]">
      <p className="font-semibold text-theme-primary mb-1">
        {payload[0]?.name}
      </p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Events</span>
        <span className="font-bold" style={{ color: payload[0]?.color }}>
          {payload[0]?.value}
        </span>
      </div>
    </div>
  );
}

// ── Skeleton loaders ──────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[540px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            {["Date", "Title", "Type", "Station", "Attendees", "Status"].map(
              (h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }).map((_, i) => (
            <tr key={i} className="border-b border-theme-subtle">
              {Array.from({ length: 6 }).map((__, j) => (
                <td key={j} className="px-4 py-3">
                  <div
                    className="skeleton h-4 rounded-md"
                    style={{ width: `${50 + ((i * 13 + j * 17) % 60)}px` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BarSkeleton({ bars = 12 }: { bars?: number }) {
  return (
    <div className="flex items-end gap-2 h-48 px-4">
      {Array.from({ length: bars }).map((_, i) => (
        <div key={i} className="flex-1">
          <div
            className="skeleton w-full rounded-t-sm"
            style={{ height: `${25 + ((i * 19 + 11) % 70)}%` }}
          />
        </div>
      ))}
    </div>
  );
}

function PieSkeleton() {
  return (
    <div className="flex items-center justify-center h-52">
      <div className="skeleton w-40 h-40 rounded-full" />
    </div>
  );
}

// ── Event status badge helper ─────────────────────────────────────────────────

function eventStatusBadge(status: string | null | undefined) {
  const s = (status ?? "").toLowerCase();
  if (s === "completed" || s === "complete")
    return <StatusBadge label="Completed" variant="green" />;
  if (s === "cancelled" || s === "canceled")
    return <StatusBadge label="Cancelled" variant="red" />;
  if (s === "scheduled" || s === "confirmed")
    return <StatusBadge label="Scheduled" variant="blue" />;
  if (s === "pending") return <StatusBadge label="Pending" variant="yellow" />;
  return <StatusBadge label={status ?? "Unknown"} variant="muted" />;
}

// ── Volume bar color by count ─────────────────────────────────────────────────

function volumeBarColor(count: number, max: number): string {
  if (max === 0) return "#3b82f6";
  const ratio = count / max;
  if (ratio >= 0.75) return "#1d4ed8"; // deep blue — highest activity
  if (ratio >= 0.5) return "#3b82f6"; // standard blue
  if (ratio >= 0.25) return "#93c5fd"; // light blue
  return "#bfdbfe"; // very light blue — low activity
}

// ── Event Table ───────────────────────────────────────────────────────────────

interface EventTableProps {
  events: CommunityEventRow[];
  isLoading: boolean;
}

function EventTable({ events, isLoading }: EventTableProps) {
  if (isLoading) return <TableSkeleton />;

  if (events.length === 0) {
    return (
      <div className="px-4 py-10 flex flex-col items-center gap-2 text-center">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-theme-muted opacity-50"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <p className="text-sm text-theme-muted">Awaiting data sync</p>
        <p className="text-xs text-theme-muted opacity-60">
          Community events will appear here once synced
        </p>
      </div>
    );
  }

  const sorted = [...events].sort((a, b) => {
    const da = a.date_start ? new Date(a.date_start).getTime() : 0;
    const db = b.date_start ? new Date(b.date_start).getTime() : 0;
    return db - da; // descending
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[540px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Date
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Title
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Type
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Station
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Attendees
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((event, idx) => {
            const upcoming = isUpcoming(event.date_start);
            return (
              <tr
                key={event.id ?? event.airtable_id ?? idx}
                className="hover:bg-theme-overlay/50 transition-colors"
              >
                <td className="px-4 py-3 text-sm text-theme-secondary whitespace-nowrap">
                  {formatDate(event.date_start)}
                </td>
                <td className="px-4 py-3 text-sm text-theme-primary font-medium">
                  <div className="flex items-center gap-2">
                    {upcoming && (
                      <StatusBadge label="Upcoming" variant="blue" />
                    )}
                    <span>{eventTitle(event) ?? "—"}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-theme-secondary">
                  {event.type_of_event ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-theme-secondary">
                  {event.station ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums">
                  {upcoming ? (
                    <span className="text-theme-muted text-xs italic">TBD</span>
                  ) : (
                    <span className="font-medium text-theme-primary">
                      {eventAttendance(event) !== null
                        ? eventAttendance(event)!.toLocaleString()
                        : "—"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">{eventStatusBadge(event.status)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Refresh indicator ─────────────────────────────────────────────────────────

function RefreshDot({ lastUpdatedAt }: { lastUpdatedAt: Date | null }) {
  if (!lastUpdatedAt) return null;
  const sec = Math.round((Date.now() - lastUpdatedAt.getTime()) / 1000);
  return (
    <div className="flex items-center gap-1.5 text-xs text-theme-muted">
      <span className="w-1.5 h-1.5 rounded-full bg-status-green shrink-0" />
      <span>Updated {sec < 5 ? "just now" : `${sec}s ago`}</span>
    </div>
  );
}

// ── Social media helpers ──────────────────────────────────────────────────────

/** Platform display config */
const PLATFORM_META: Record<
  string,
  { label: string; color: string; accentClass: string }
> = {
  meta: {
    label: "Facebook / Meta",
    color: "#1877f2",
    accentClass: "text-blue-500",
  },
  linkedin: {
    label: "LinkedIn",
    color: "#0a66c2",
    accentClass: "text-sky-600",
  },
  x: {
    label: "X (Twitter)",
    color: "#000000",
    accentClass: "text-gray-800 dark:text-gray-200",
  },
};

function platformLabel(platform: string): string {
  return PLATFORM_META[platform]?.label ?? platform;
}

/** Parse a social metric data JSON blob safely */
function parseData(row: SocialMetricRow): Record<string, unknown> {
  try {
    return JSON.parse(row.data) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Compute engagement for a post/tweet row */
function engagementForRow(row: SocialMetricRow): number {
  const d = parseData(row);
  if (row.platform === "meta") {
    return asNumber(d.likes) + asNumber(d.comments) + asNumber(d.shares);
  }
  if (row.platform === "x") {
    return (
      asNumber(d.like_count) +
      asNumber(d.retweet_count) +
      asNumber(d.reply_count) +
      asNumber(d.quote_count)
    );
  }
  return 0;
}

/** Get the post/tweet text preview */
function postPreview(row: SocialMetricRow): string {
  const d = parseData(row);
  if (row.platform === "meta")
    return String(d.message ?? "").slice(0, 120) || "—";
  if (row.platform === "x") return String(d.text ?? "").slice(0, 120) || "—";
  return "—";
}

// ── Social KPI card skeleton ──────────────────────────────────────────────────

function SocialKPISkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="glass rounded-xl p-5 space-y-3">
          <div className="skeleton h-4 w-24 rounded" />
          <div className="skeleton h-8 w-16 rounded" />
          <div className="skeleton h-3 w-32 rounded" />
        </div>
      ))}
    </div>
  );
}

// ── Recent posts table skeleton ───────────────────────────────────────────────

function PostsSkeleton() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            {["Date", "Platform", "Post", "Likes", "Engagement"].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }).map((_, i) => (
            <tr key={i} className="border-b border-theme-subtle">
              {Array.from({ length: 5 }).map((__, j) => (
                <td key={j} className="px-4 py-3">
                  <div
                    className="skeleton h-4 rounded"
                    style={{ width: `${40 + ((i * 11 + j * 17) % 60)}px` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CommunityEngagement() {
  const INTERVAL = 120_000;

  const [range, setRange] = useState<DateRange>("all");
  const sinceDate = useSinceDate(range);

  const {
    data: eventsData,
    isLoading: eventsLoading,
    lastUpdatedAt,
  } = useCommunityEvents(INTERVAL);
  const { data: aggregatesData, isLoading: aggLoading } =
    useAggregates(INTERVAL);
  const { data: socialSummaryData, isLoading: socialSummaryLoading } =
    useSocialSummary(INTERVAL);
  const { data: socialMetricsData, isLoading: socialMetricsLoading } =
    useSocialMetrics({ metric_type: "post" }, INTERVAL);
  const { data: xMetricsData } = useSocialMetrics(
    { platform: "x", metric_type: "tweet" },
    INTERVAL,
  );

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  const allEvents = eventsData?.data ?? [];
  const agg = aggregatesData?.aggregates ?? {};

  // ── Date range filter: filter events by date_start ────────────────────────
  const events = useMemo<CommunityEventRow[]>(() => {
    if (!sinceDate) return allEvents;
    return allEvents.filter((e) => isAfterSince(e.date_start ?? "", sinceDate));
  }, [allEvents, sinceDate]);

  // ── KPI values ──────────────────────────────────────────────────────────────
  const eventsYtd = asNumber(agg["community_events_ytd"]);
  const reachYtd = asNumber(agg["community_reach_ytd"]);
  const avgAttendance = eventsYtd > 0 ? Math.round(reachYtd / eventsYtd) : 0;
  const upcomingCount = useMemo(
    () => events.filter((e) => isUpcoming(e.date_start)).length,
    [events],
  );

  // ── Chart data ──────────────────────────────────────────────────────────────
  const monthlyVolume = useMemo(() => buildMonthlyVolume(events), [events]);
  const typeDistribution = useMemo(
    () => buildTypeDistribution(events),
    [events],
  );
  const stationActivity = useMemo(() => buildStationActivity(events), [events]);

  const maxMonthCount = useMemo(
    () => Math.max(...monthlyVolume.map((m) => m.count), 1),
    [monthlyVolume],
  );

  // Custom bar fill per cell for volume chart
  const volumeBarData = useMemo(
    () =>
      monthlyVolume.map((m) => ({
        ...m,
        fill: volumeBarColor(m.count, maxMonthCount),
      })),
    [monthlyVolume, maxMonthCount],
  );

  // ── KPI accent/trend helpers ────────────────────────────────────────────────
  const upcomingAccent = upcomingCount > 0 ? "green" : "neutral";

  // ── Social data ─────────────────────────────────────────────────────────────
  const socialLoading = socialSummaryLoading || socialMetricsLoading;
  const hasAnyData = socialSummaryData?.has_data ?? false;

  // Combine Meta posts + X tweets into a single sorted list of recent posts
  const recentPosts = useMemo(() => {
    const metaPosts = (socialMetricsData?.metrics ?? []).filter(
      (r) => r.metric_type === "post",
    );
    const tweets = (xMetricsData?.metrics ?? []).filter(
      (r) => r.metric_type === "tweet",
    );
    return [...metaPosts, ...tweets]
      .sort((a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : 0))
      .slice(0, 20);
  }, [socialMetricsData, xMetricsData]);

  const metaSummary = socialSummaryData?.summary.meta ?? null;
  const linkedinSummary = socialSummaryData?.summary.linkedin ?? null;
  const xSummary = socialSummaryData?.summary.x ?? null;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Community Engagement
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Community outreach events, reach metrics, and station engagement
            activity.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="Live" variant="green" />
          <RefreshDot lastUpdatedAt={lastUpdatedAt} />
        </div>
      </div>

      {/* ── Date Range Toggle ─────────────────────────────────────────────── */}
      <DateRangeToggle value={range} onChange={setRange} />

      {/* ── KPI Row ──────────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Year-to-Date Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard
            label="Events YTD"
            value={aggLoading ? "—" : eventsYtd}
            subtitle="community events held this year"
            accent="neutral"
            isLoading={aggLoading}
            trend={
              !aggLoading && eventsYtd > 0
                ? {
                    direction: "up",
                    label: `${eventsYtd} events`,
                    positiveIsGood: true,
                  }
                : undefined
            }
          />

          <KPICard
            label="Total Reach YTD"
            value={aggLoading ? "—" : reachYtd.toLocaleString()}
            subtitle="cumulative attendees this year"
            accent="green"
            isLoading={aggLoading}
            trend={
              !aggLoading && reachYtd > 0
                ? {
                    direction: "up",
                    label: `${reachYtd.toLocaleString()} reached`,
                    positiveIsGood: true,
                  }
                : undefined
            }
          />

          <KPICard
            label="Avg Attendance"
            value={aggLoading ? "—" : eventsYtd > 0 ? avgAttendance : "No data"}
            subtitle="attendees per event this year"
            accent="neutral"
            isLoading={aggLoading}
          />

          <KPICard
            label="Upcoming Events"
            value={eventsLoading ? "—" : upcomingCount}
            subtitle="scheduled in the future"
            accent={upcomingAccent}
            isLoading={eventsLoading}
            trend={
              !eventsLoading
                ? {
                    direction: upcomingCount > 0 ? "up" : "down",
                    label:
                      upcomingCount > 0
                        ? `${upcomingCount} on schedule`
                        : "None scheduled",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />
        </div>
      </section>

      {/* ── Section 1: Event Calendar / List ─────────────────────────────── */}
      <section>
        <ChartCard
          title="Event Calendar"
          subtitle="All community events sorted by date — upcoming events highlighted with blue badge"
          headerRight={
            eventsLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : eventsData ? (
              <StatusBadge
                label={`${eventsData.count} events`}
                variant="muted"
              />
            ) : null
          }
          minHeight={0}
        >
          <EventTable events={events} isLoading={eventsLoading} />
        </ChartCard>
      </section>

      {/* ── Section 2: Event Volume Trend ────────────────────────────────── */}
      <section>
        <ChartCard
          title="Event Volume Trend"
          subtitle="Events per month over the past 12 months — darker bars indicate higher activity"
          minHeight={220}
          empty={!eventsLoading && events.length === 0}
        >
          {eventsLoading ? (
            <BarSkeleton bars={12} />
          ) : events.length === 0 ? (
            <div className="flex items-center justify-center gap-2 text-center">
              <p className="text-sm text-theme-muted">Awaiting data sync</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={volumeBarData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke={gridColor}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                  interval={1}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                  width={28}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<VolumeTooltip />}
                  cursor={{
                    fill: isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
                <Bar
                  dataKey="count"
                  name="Events"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                >
                  {volumeBarData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </section>

      {/* ── Sections 3 & 4: Type Distribution + Station Activity ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 3: Event Type Distribution */}
        <ChartCard
          title="Event Type Distribution"
          subtitle="Breakdown by event category"
          minHeight={260}
          empty={!eventsLoading && typeDistribution.length === 0}
        >
          {eventsLoading ? (
            <PieSkeleton />
          ) : typeDistribution.length === 0 ? (
            <div className="flex items-center justify-center gap-2">
              <p className="text-sm text-theme-muted">No event type data</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={typeDistribution}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name: _name, percent }) =>
                    `${((percent ?? 0) * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {typeDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        PIE_COLORS[entry.name] ??
                        PIE_FALLBACK_COLORS[index % PIE_FALLBACK_COLORS.length]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  wrapperStyle={{
                    fontSize: 11,
                    color: axisColor,
                    paddingTop: 8,
                  }}
                  iconSize={10}
                  iconType="circle"
                  formatter={(value) => (
                    <span style={{ color: axisColor }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Section 4: Station Activity */}
        <ChartCard
          title="Station Activity"
          subtitle="Event count per station — identifies most active community stations"
          minHeight={260}
          empty={!eventsLoading && stationActivity.length === 0}
        >
          {eventsLoading ? (
            <BarSkeleton bars={6} />
          ) : stationActivity.length === 0 ? (
            <div className="flex items-center justify-center gap-2">
              <p className="text-sm text-theme-muted">
                No station data available
              </p>
            </div>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={Math.max(260, stationActivity.length * 42 + 40)}
            >
              <BarChart
                data={stationActivity}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 0, bottom: 4 }}
              >
                <CartesianGrid
                  horizontal={false}
                  stroke={gridColor}
                  strokeDasharray="3 3"
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="station"
                  tick={{ fontSize: 11, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                  width={90}
                />
                <Tooltip
                  content={<StationTooltip />}
                  cursor={{
                    fill: isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
                <Bar
                  dataKey="count"
                  name="Events"
                  fill="#3b82f6"
                  fillOpacity={0.8}
                  radius={[0, 3, 3, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* ── Social Media ─────────────────────────────────────────────────── */}
      <section className="space-y-6">
        {/* Section header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-theme-primary">
              Social Media Presence
            </h2>
            <p className="text-xs text-theme-muted mt-0.5">
              Follower counts, recent posts, and engagement across Meta,
              LinkedIn, and X.
            </p>
          </div>
          {!socialLoading && (
            <StatusBadge
              label={hasAnyData ? "Live" : "Sync Pending"}
              variant={hasAnyData ? "green" : "yellow"}
            />
          )}
        </div>

        {/* Pending state */}
        {!socialLoading && !hasAnyData && (
          <div className="glass rounded-xl px-6 py-6 flex flex-col items-center justify-center gap-2 text-center">
            <p className="text-sm font-medium text-theme-secondary">
              Social metrics coming soon
            </p>
            <p className="text-xs text-theme-muted max-w-md">
              Follower counts, posts, and engagement across Meta, LinkedIn, and
              X will appear here once the social connector is live.
            </p>
          </div>
        )}

        {/* Platform follower KPI cards */}
        {(socialSummaryLoading || hasAnyData) && (
          <>
            {socialSummaryLoading ? (
              <SocialKPISkeleton />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Meta */}
                <div className="glass rounded-xl p-5 space-y-1">
                  <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
                    {platformLabel("meta")}
                  </p>
                  <p className="text-2xl font-bold text-blue-500 tabular-nums">
                    {metaSummary?.followers != null
                      ? metaSummary.followers.toLocaleString()
                      : "—"}
                  </p>
                  <p className="text-xs text-theme-muted">Page fans</p>
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-theme-subtle">
                    <span className="text-xs text-theme-muted">
                      Posts (30d)
                    </span>
                    <span className="text-xs font-semibold text-theme-primary ml-auto">
                      {metaSummary?.post_count ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-theme-muted">
                      Engagement (30d)
                    </span>
                    <span className="text-xs font-semibold text-theme-primary ml-auto">
                      {(metaSummary?.total_engagement ?? 0).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="glass rounded-xl p-5 space-y-1">
                  <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
                    {platformLabel("linkedin")}
                  </p>
                  <p className="text-2xl font-bold text-sky-600 tabular-nums">
                    {linkedinSummary?.followers != null
                      ? linkedinSummary.followers.toLocaleString()
                      : "—"}
                  </p>
                  <p className="text-xs text-theme-muted">Followers</p>
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-theme-subtle">
                    <span className="text-xs text-theme-muted">API access</span>
                    <span className="text-xs font-semibold ml-auto">
                      {linkedinSummary?.credential_valid === true ? (
                        <StatusBadge label="Valid" variant="green" />
                      ) : linkedinSummary?.credential_valid === false ? (
                        <StatusBadge label="Error" variant="red" />
                      ) : (
                        <StatusBadge label="Pending" variant="yellow" />
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-theme-muted">Analytics</span>
                    <span className="text-xs text-theme-muted ml-auto italic">
                      Requires Community Mgmt API
                    </span>
                  </div>
                </div>

                {/* X */}
                <div className="glass rounded-xl p-5 space-y-1">
                  <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
                    {platformLabel("x")}
                  </p>
                  <p className="text-2xl font-bold text-theme-primary tabular-nums">
                    {xSummary?.followers != null
                      ? xSummary.followers.toLocaleString()
                      : "—"}
                  </p>
                  <p className="text-xs text-theme-muted">Followers</p>
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-theme-subtle">
                    <span className="text-xs text-theme-muted">
                      Tweets (30d)
                    </span>
                    <span className="text-xs font-semibold text-theme-primary ml-auto">
                      {xSummary?.post_count ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-theme-muted">
                      Engagement (30d)
                    </span>
                    <span className="text-xs font-semibold text-theme-primary ml-auto">
                      {(xSummary?.total_engagement ?? 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Recent posts table */}
            <ChartCard
              title="Recent Posts"
              subtitle="Latest posts across Meta and X, sorted by date — engagement totals include likes, comments, shares, and retweets"
              headerRight={
                socialMetricsLoading ? (
                  <span className="text-xs text-theme-muted animate-pulse">
                    Loading...
                  </span>
                ) : hasAnyData ? (
                  <StatusBadge
                    label={`${recentPosts.length} posts`}
                    variant="muted"
                  />
                ) : null
              }
              minHeight={0}
            >
              {socialMetricsLoading ? (
                <PostsSkeleton />
              ) : recentPosts.length === 0 ? (
                <div className="px-4 py-10 text-center text-theme-muted text-sm">
                  No recent posts found in the last 30 days.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-left">
                    <thead>
                      <tr className="border-b border-theme-subtle">
                        <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                          Platform
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
                          Post
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
                          Likes
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
                          Engagement
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentPosts.map((row) => {
                        const d = parseData(row);
                        const likes =
                          row.platform === "meta"
                            ? asNumber(d.likes)
                            : asNumber(d.like_count);
                        const engagement = engagementForRow(row);
                        return (
                          <tr
                            key={row.id}
                            className="hover:bg-theme-overlay/50 transition-colors"
                          >
                            <td className="px-4 py-3 text-sm text-theme-secondary whitespace-nowrap">
                              {formatDate(row.date)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <StatusBadge
                                label={row.platform === "meta" ? "Meta" : "X"}
                                variant={
                                  row.platform === "meta" ? "blue" : "muted"
                                }
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-theme-secondary max-w-xs truncate">
                              {postPreview(row)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right tabular-nums font-medium text-theme-primary">
                              {likes.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right tabular-nums font-medium text-theme-primary">
                              {engagement.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </ChartCard>
          </>
        )}
      </section>
    </div>
  );
}
